import { useMemo } from "react"
import type { PreOrderFlow } from "@stockline/shared/types/orders/pre-order-flow"
import { offlineDb } from "../../../lib/offline/db"
import { PRE_ORDER_FLOW_TABLE } from "../../../lib/offline/resources/orders/pre-order-flow/db"
import { useSharedLiveQuery } from "../../../lib/offline/use-shared-live-query"

export interface UsePreOrderFlowsFilter {
  preOrderId?: string
  status?: number
  productId?: string
  /**
   * When passed alongside `productId`, the hook uses the
   * `[preOrderId+productId]` compound index added in DB v31 to fetch only the
   * flows whose `(preOrderId, productId)` falls in the provided set — the
   * product-view leaf query for the recap, scoped to the active date's POs
   * without a full-table scan. Ignored when `productId` is missing.
   */
  preOrderIds?: string[]
  includeDeleted?: boolean
  /**
   * When true the hook bypasses the per-filter Dexie subscription and
   * returns a stable empty array. All `skip: true` callers share a single
   * sentinel cache entry so the cost is one subscription total, not one
   * per call site. Use for synthetic ids (e.g. AI-proposal virtual
   * pre-orders) that will never appear in `preOrderFlows` and would
   * otherwise register a permanently-empty live query that re-fires on
   * every table change.
   */
  skip?: boolean
}

const EMPTY_FLOWS: PreOrderFlow[] = []
const SKIP_CACHE_KEY = "preOrderFlows:__skip__"
const skipFactory = (): PreOrderFlow[] => EMPTY_FLOWS

export function usePreOrderFlows(
  filter?: UsePreOrderFlowsFilter
): PreOrderFlow[] {
  // Stable cache key for the live-query effect — a fresh array reference on
  // every parent render must not re-subscribe when the contents are equal.
  const preOrderIdsKey = useMemo(
    () => (filter?.preOrderIds ?? []).slice().sort().join(","),
    [filter?.preOrderIds]
  )

  const usingCompound = !!(filter?.productId && filter?.preOrderIds)
  const skip = filter?.skip ?? false

  // Cache key for the cross-mount shared observable. Two callers passing
  // the same filter dimensions share one Dexie subscription, so a partner
  // card scrolling out + back (or a collapsible toggle) reuses the cached
  // value with zero IDB roundtrip. `includeDeleted` is deliberately
  // excluded — it's a JS post-filter, doesn't change the underlying query.
  // Skip routes through a single sentinel key so every skipped caller
  // shares one no-op entry instead of registering its own dead live query.
  const cacheKey = skip
    ? SKIP_CACHE_KEY
    : usingCompound
      ? `preOrderFlows:compound:${filter!.productId}:${preOrderIdsKey}`
      : filter?.preOrderId
        ? `preOrderFlows:po:${filter.preOrderId}`
        : filter?.productId
          ? `preOrderFlows:product:${filter.productId}`
          : filter?.status !== undefined
            ? `preOrderFlows:status:${filter.status}`
            : `preOrderFlows:all`

  const preOrderId = filter?.preOrderId
  const productId = filter?.productId
  const status = filter?.status
  const preOrderIds = filter?.preOrderIds

  const rows = useSharedLiveQuery<PreOrderFlow[]>(
    cacheKey,
    skip
      ? skipFactory
      : async () => {
          const table = offlineDb.table<PreOrderFlow>(PRE_ORDER_FLOW_TABLE)
          // `flowIndex` is not a standalone Dexie index (only present in the
          // compound `[preOrderId+productId+createdAt]`/etc indexes) and
          // adding one would bump `DB_VERSION` for every cold-start. Dexie's
          // `sortBy("flowIndex")` would still work indirectly, but it sorts
          // `undefined < 0`, so legacy rows whose `flowIndex` hasn't been
          // backfilled would jump to the top instead of treating `undefined`
          // as `0`. Doing the sort in JS keeps every filter path consistent
          // with the no-filter path's `?? 0` fallback.
          const sortByFlowIndex = (arr: PreOrderFlow[]): PreOrderFlow[] =>
            arr.sort((a, b) => {
              const ai = a.flowIndex ?? 0
              const bi = b.flowIndex ?? 0
              if (ai !== bi) return ai - bi
              return (a.createdAt ?? "").localeCompare(b.createdAt ?? "")
            })
          // Compound path: (preOrderIds × productId) via the v31 compound index.
          // `anyOf` over the pair list is a multi-point lookup on the index, so
          // the cost scales with the matching row count, not the table size.
          if (productId && preOrderIds) {
            if (preOrderIds.length === 0) return []
            const rows = await table
              .where("[preOrderId+productId]")
              .anyOf(preOrderIds.map((id) => [id, productId]))
              .toArray()
            return sortByFlowIndex(rows)
          }
          if (preOrderId) {
            const rows = await table
              .where("preOrderId")
              .equals(preOrderId)
              .toArray()
            return sortByFlowIndex(rows)
          }
          if (productId) {
            const rows = await table
              .where("productId")
              .equals(productId)
              .toArray()
            return sortByFlowIndex(rows)
          }
          if (status !== undefined) {
            const rows = await table.where("status").equals(status).toArray()
            return sortByFlowIndex(rows)
          }
          return sortByFlowIndex(await table.toArray())
        }
  )

  // Memoise the tombstone filter — keeps the array reference stable between
  // renders so downstream useMemo deps don't invalidate on every re-render.
  // (Recap's filter clicks rerender the tree; without this, the offline-first
  // shim's expensive map/sort/enrichment would re-run for nothing.)
  const includeDeleted = filter?.includeDeleted
  const liveRows = rows ?? EMPTY_FLOWS
  return useMemo(
    () => (includeDeleted ? liveRows : liveRows.filter((r) => !r.deletedAt)),
    [liveRows, includeDeleted]
  )
}
